import { Box, Card, Grid, Link, Paper, Table, TableBody, TableContainer, CircularProgress } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { Crumbs, DataRow, DateDisplay, MonoText, StatusLabel, Title } from 'app/components';
import { API } from 'app/services';
import { useRouter, useToastCatcher } from 'app/utils';
import clsx from 'clsx';
import React, { useEffect, useState, useRef } from 'react';
import { useSelector } from 'react-redux';
import { Link as BrowserLink } from 'react-router-dom';
import { Refresh as RefreshIcon } from '@material-ui/icons';

import { RegtankInfoTable, RegtankRiskTable, RegtankLivenessTable, RegtankDocTable, DownloadDocumentPreview } from './components';

const useStyles = makeStyles((theme) => ({
  root: {},
  underline: {
    textDecoration: 'underline',
  },
  topSpace: {
    marginTop: theme.spacing(2),
  },
}));

function DocumentDetailComp(props) {
  const { routeType = 'account', documentResult, className } = props;
  const classes = useStyles();

  const [document, setDocument] = useState(documentResult);
  const [info, setInfo] = useState(null);
  const [summary, setSummary] = useState(null);
  const [risk, setRisk] = useState(null);
  const [kyc, setKyc] = useState(null);
  const [docResult, setDocResult] = useState(null);
  const [docField, setDocField] = useState(null);
  const [docImage, setDocImage] = useState(null);
  const [doc, setDoc] = useState(null);
  const [liveness, setLiveness] = useState(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [printPreview, setPrintPreview] = useState(false);
  const [session, setSession] = useState(null);

  const access = useSelector((state) => state.profile.access);
  const [toaster, errorCatcher] = useToastCatcher({
    errorParser: null,
    taskname: 'getDocument',
  });

  const router = useRouter();
  const { params } = router.match;

  const doc_id = params.document_id || documentResult?.model?.id;

  const mountedRef = useRef(false);
  const isFetching = useRef(false);
  const hasFetched = useRef(false);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (access?.token) getDocumentDetail();
    // eslint-disable-next-line
  }, [access?.token]);

  const getCaseDetailPath = (id) => {
    return routeType === 'account' ? `/screenings/cases/${id}/detail` : `/admin/screenings/cases/${id}/detail`;
  };

  const getDocumentDetail = (refresh = false) => {
    if (!doc_id) return toaster({ message: 'invalid id' });
    if (isFetching.current) return;
    if (hasFetched.current && !refresh) return;
    isFetching.current = true;
    hasFetched.current = true;

    errorCatcher(async () => {
      try {
        const loader = routeType === 'account' ? API.Screening.get_document : API.Admin.get_verify_detail;

        const queryParams = refresh ? { refresh: true } : null;

        let { model } = await loader(doc_id, queryParams);

        // ✅ Show initial data immediately, even if still verifying
        if (mountedRef.current) {
          setDocument(model);
        }

        while (model.status === 'verifying') {
          await new Promise((resolve) => setTimeout(resolve, 5000));
          if (!mountedRef.current) break;

          const response = await loader(doc_id, queryParams);
          model = response.model;

          // ✅ Update document on each poll so UI reflects latest state
          if (mountedRef.current) {
            setDocument(model);
          }
        }

        if (mountedRef.current) {
          const { berbix_refresh_token } = model;

          if (berbix_refresh_token && berbix_refresh_token.toLowerCase().includes('regtank')) {
            try {
              const loader = routeType === 'account' ? API.Users.get_regtank_onboarding_data : API.Admin.get_regtank_onboarding_data;
              const res = await loader(model.verification_request_id, queryParams);
              if (res.model.docContent?.content) {
                const docData = res.model.docContent?.content?.data || {};

                const image = {
                  front: {
                    ...docData.info.frontImageQuality,
                    securityCheck: docData.info.frontSecurityCheck,
                  },
                  back: {
                    ...docData.info.backImageQuality,
                    securityCheck: docData.info.backSecurityCheck,
                  },
                };
                setDocResult(docData.info?.status?.detailsOpticalStatus);
                setDocField(docData.info?.documentFields);
                setDocImage(image);
              }
              setInfo({ ...res.model.info, ...res.model.user });
              setSummary(res.model.summary);
              setRisk(res.model.risk);
              setKyc(res.model.kyc);
              setLiveness(res.model.liveness);
              setDoc(res.model.documents);
              setSession(res.model.session);
            } catch (err) {
              console.error('regtank error:', err);
            }
          }

          setTimeout(() => window.scrollTo(0, 0), 200);
        }
      } finally {
        isFetching.current = false;
        setIsRefreshing(false);
      }
    });
  };

  const handleRefresh = () => {
    if (isRefreshing || isFetching.current) return;
    setIsRefreshing(true);
    hasFetched.current = false;
    getDocumentDetail(true);
  };

  const getButtons = () => {
    const arr = [];

    arr.push({
      space: 3,
      onClick: handleRefresh,
      text: 'Refresh',
      icon: <RefreshIcon />,
      loading: isRefreshing,
      disabled: isFetching.current && !isRefreshing,
    });

    // always show download
    arr.push({
      space: 3,
      onClick: () => setPrintPreview(true),
      text: 'Download Report',
    });

    if (routeType !== 'account' || documentResult) return arr;

    arr.push({
      space: 3,
      onClick: () => router.history.push(`/document-verify`),
      text: 'Verify new document',
    });

    if (document) {
      arr.push({
        space: 3,
        onClick: () => router.history.push(`/screenings/create?case=${document.case?.reference}&document=${params.document_id}`),
        text: 'Use document for screening',
      });
    }

    return arr;
  };

  const status = (summary?.status || document?.status || '').toLowerCase();
  return (
    <Box className={clsx(classes.root, className)} minWidth={'80%'}>
      {/* ================= NORMAL VIEW ================= */}
      {!printPreview && (
        <>
          <Crumbs
            current="Detail"
            links={[
              {
                text: 'Documents',
                href: routeType === 'admin' ? '/admin/documents' : '/documents',
              },
            ]}
          />

          <Title prefix="Document detail" floatButtons={getButtons()}>
            Powered by Regtank
          </Title>

          <Grid container spacing={2}>
            {/* 🔥 LEFT COLUMN */}
            <Grid item lg={6} sm={12}>
              {/* 🔥 SUMMARY CARD */}
              <Card className={classes.topSpace}>
                <TableContainer component={Paper}>
                  <Table>
                    <TableBody>
                      <DataRow name="Document ID" value={<MonoText>{summary?.documentId || document?.reference || '-'}</MonoText>} />

                      <DataRow name="Type" value={<MonoText>{summary?.type || document?.type || '-'}</MonoText>} />

                      <DataRow
                        name="Status"
                        value={
                          <Box display="flex" alignItems="center">
                            <StatusLabel
                              value={summary?.status || document?.status}
                              variantMap={{
                                approved: 'success',
                                complete: 'success',
                                uploaded: 'success',
                                verifying: 'warning',
                                rejected: 'error',
                                failed: 'error',
                              }}>
                              {summary?.status || document?.status || '-'}
                            </StatusLabel>
                            {/* 🔥 only when verifying */}
                            {status === 'verifying' && (isRefreshing ? <CircularProgress size={16} style={{ marginLeft: 8 }} /> : <RefreshIcon style={{ marginLeft: 8, cursor: 'pointer' }} onClick={handleRefresh} />)}
                          </Box>
                        }
                      />
                      <DataRow name="Requested Date" value={<DateDisplay date={summary?.createdDate || document?.created_at} />} />

                      <DataRow
                        name="Case ID"
                        wrapperProps={{
                          underline: 'always',
                          to: getCaseDetailPath(document?.case?.id),
                          component: BrowserLink,
                        }}
                        wrapper={Link}
                        value={<MonoText className={classes.underline}>{document?.case?.reference || '-'}</MonoText>}
                      />
                    </TableBody>
                  </Table>
                </TableContainer>
              </Card>

              {/* 🔥 BASIC INFO */}
              <RegtankInfoTable className={classes.topSpace} info={info || {}} session={session || {}} type = {summary?.type}/>

              {summary?.type !== 'upload' && <RegtankLivenessTable className={classes.topSpace} liveness={liveness || {}} />}
            </Grid>

            {/* 🔥 RIGHT COLUMN */}
            <Grid item lg={6} sm={12}>
              <RegtankRiskTable className={classes.topSpace} kyc={kyc || {}} risk={risk || {}} opticalStatus={docResult || {}} type={summary?.type || document?.verification?.type || ''} />
              <RegtankDocTable className={classes.topSpace} documents={doc || {}} docField={docField || {}} docImage={docImage || {}} />
            </Grid>
          </Grid>
        </>
      )}

      {/* ================= PRINT PREVIEW ================= */}
      {printPreview && (
        <DownloadDocumentPreview
          cancelPrint={() => setPrintPreview(false)}
          docField={docField || {}}
          docImage={docImage || {}}
          type={summary?.type || document?.verification?.type || ''}
          opticalStatus={docResult || {}}
          document={document}
          session={session}
          summary={summary}
          info={info}
          risk={risk}
          kyc={kyc}
          liveness={liveness}
          documents={doc}
        />
      )}
    </Box>
  );
}

export default DocumentDetailComp;
